import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Phone, Mail, MessageCircle, MoreHorizontal, ArrowLeftRight } from "lucide-react";
import * as api from "../../lib/crmApi";
import CrmLeadDrawer from "./CrmLeadDrawer";
import NoteModal from "./NoteModal";

const KIND_META = {
    ringt: { label: "Ringet", color: "bg-sky-100 text-sky-800 border-sky-200", icon: Phone },
    snakket: { label: "Snakket", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: MessageCircle },
    mail: { label: "Mail", color: "bg-violet-100 text-violet-800 border-violet-200", icon: Mail },
    andet: { label: "Andet", color: "bg-slate-100 text-slate-700 border-slate-200", icon: MoreHorizontal },
};

function startOfWeek(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7; // Mon=0
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}
function fmtDay(d) { return d.toISOString().slice(0, 10); }
function fmtTime(iso) { return iso ? new Date(iso).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" }) : ""; }
function fmtDayLabel(d) { return d.toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short" }); }
function fmtMonthLabel(d) { return d.toLocaleDateString("da-DK", { month: "long", year: "numeric" }); }

export default function CrmFollowups() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("week");
    const [anchor, setAnchor] = useState(new Date());
    const [openLead, setOpenLead] = useState(null);
    const [openNote, setOpenNote] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const range = useMemo(() => {
        if (view === "week") {
            const start = startOfWeek(anchor);
            const end = new Date(start); end.setDate(start.getDate() + 7);
            return { start, end };
        } else {
            const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
            const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
            return { start, end };
        }
    }, [view, anchor]);

    useEffect(() => {
        setLoading(true);
        api.listFollowups({ from_: range.start.toISOString(), to: range.end.toISOString() })
            .then(setItems)
            .catch(() => toast.error("Kunne ikke hente opfølgninger"))
            .finally(() => setLoading(false));
    }, [range.start, range.end, reloadKey]);

    const byDay = useMemo(() => {
        const map = {};
        items.forEach((it) => {
            const at = it.payload?.followup_at;
            if (!at) return;
            const day = at.slice(0, 10);
            (map[day] = map[day] || []).push(it);
        });
        Object.values(map).forEach((arr) => arr.sort((a, b) =>
            (a.payload.followup_at || "").localeCompare(b.payload.followup_at || "")));
        return map;
    }, [items]);

    const shift = (dir) => {
        const next = new Date(anchor);
        if (view === "week") next.setDate(next.getDate() + dir * 7);
        else next.setMonth(next.getMonth() + dir);
        setAnchor(next);
    };

    return (
        <div className="p-6 lg:p-8" data-testid="crm-followups">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900">Opfølgninger</h1>
                    <p className="text-sm text-slate-500 mt-1">Alle noter med opfølgningsdato — samlet i kalender</p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setView("week")}
                        data-testid="crm-cal-view-week"
                        className={`px-3 h-9 rounded-lg text-xs font-semibold border ${view === "week" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"}`}>Uge</button>
                    <button type="button" onClick={() => setView("month")}
                        data-testid="crm-cal-view-month"
                        className={`px-3 h-9 rounded-lg text-xs font-semibold border ${view === "month" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"}`}>Måned</button>
                    <button type="button" onClick={() => setAnchor(new Date())}
                        className="px-3 h-9 rounded-lg text-xs font-semibold border bg-white text-slate-600 border-slate-300 hover:bg-slate-50">I dag</button>
                    <div className="flex">
                        <button type="button" onClick={() => shift(-1)} className="px-2 h-9 rounded-l-lg border border-slate-300 bg-white hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
                        <button type="button" onClick={() => shift(1)} className="px-2 h-9 rounded-r-lg border-y border-r border-slate-300 bg-white hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                </div>
            </div>

            <div className="mb-4 text-sm font-semibold text-slate-700">
                {view === "week"
                    ? `Uge fra ${fmtDayLabel(range.start)}`
                    : fmtMonthLabel(anchor)}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : view === "week" ? (
                <WeekGrid start={range.start} byDay={byDay} onOpenNote={setOpenNote} />
            ) : (
                <MonthGrid anchor={anchor} byDay={byDay} onOpenNote={setOpenNote} />
            )}

            {items.length === 0 && !loading && (
                <div className="mt-6 text-center text-sm text-slate-400 py-8">Ingen opfølgninger i denne periode</div>
            )}

            {openNote && (
                <NoteModal activity={openNote} leadId={openNote.lead_id} lead={openNote.lead}
                    onOpenLead={() => { setOpenLead(openNote.lead_id); setOpenNote(null); }}
                    onClose={() => setOpenNote(null)}
                    onDone={() => { setOpenNote(null); setReloadKey((k) => k + 1); }} />
            )}
            {openLead && <CrmLeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} onChanged={() => setReloadKey((k) => k + 1)} />}
        </div>
    );
}

function WeekGrid({ start, byDay, onOpenNote }) {
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    return (
        <div className="grid grid-cols-7 gap-2">
            {days.map((d) => {
                const key = fmtDay(d);
                const isToday = fmtDay(new Date()) === key;
                return (
                    <div key={key} className={`min-h-[240px] rounded-lg border p-2 ${isToday ? "bg-sky-50 border-sky-300" : "bg-white border-slate-200"}`}>
                        <div className="text-xs font-semibold text-slate-700 mb-2">{fmtDayLabel(d)}</div>
                        <div className="space-y-1.5">
                            {(byDay[key] || []).map((it) => <FollowupCard key={it.id} it={it} onOpenNote={onOpenNote} />)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function MonthGrid({ anchor, byDay, onOpenNote }) {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
    const monthNum = anchor.getMonth();
    return (
        <div>
            <div className="grid grid-cols-7 gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"].map((d) => <div key={d} className="px-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-2">
                {cells.map((d, i) => {
                    const key = fmtDay(d);
                    const inMonth = d.getMonth() === monthNum;
                    const isToday = fmtDay(new Date()) === key;
                    return (
                        <div key={i} className={`min-h-[100px] rounded-lg border p-1.5 ${
                            isToday ? "bg-sky-50 border-sky-300" :
                            inMonth ? "bg-white border-slate-200" :
                            "bg-slate-50 border-slate-100 opacity-60"
                        }`}>
                            <div className="text-[11px] font-semibold text-slate-500 mb-1">{d.getDate()}</div>
                            <div className="space-y-1">
                                {(byDay[key] || []).slice(0, 3).map((it) => <FollowupCard key={it.id} it={it} onOpenNote={onOpenNote} compact />)}
                                {(byDay[key] || []).length > 3 && <div className="text-[10px] text-slate-500">+{(byDay[key] || []).length - 3} mere</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function FollowupCard({ it, onOpenNote, compact }) {
    const kind = KIND_META[it.payload?.kind || "andet"] || KIND_META.andet;
    const Icon = kind.icon;
    return (
        <button type="button" onClick={() => onOpenNote(it)} title={it.payload?.text || ""}
            data-testid={`crm-followup-${it.id}`}
            className={`w-full text-left rounded border ${kind.color} px-2 py-1 hover:brightness-95 transition-all`}>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                <Icon className="h-3 w-3 flex-shrink-0" />
                <span>{fmtTime(it.payload?.followup_at)}</span>
                {!compact && <span className="ml-auto text-[9px] font-medium">{kind.label}</span>}
            </div>
            <div className={`font-semibold truncate ${compact ? "text-[10px]" : "text-xs"}`}>{it.lead?.company}</div>
            {!compact && <div className="text-[10px] text-slate-600 truncate">{it.lead?.contact_name}</div>}
            {!compact && it.payload?.text && <div className="text-[10px] text-slate-500 truncate mt-0.5 italic">{it.payload.text}</div>}
        </button>
    );
}
